import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { updateClassifierRequest } from './requests';
import { UpdateClassifierRequestProps } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/types';
import { ApiWrapperError } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { ErrorCode } from '@dealstack/shared/lib/common/errors';

export const useUpdateClassifier = (dealId: string) => {
	const queryClient = useQueryClient();
	const tAdminDashboard = useTranslationAdminDashboard();

	const updateClassifier = useMutation<
		Awaited<ReturnType<typeof updateClassifierRequest>>,
		ApiWrapperError<ErrorCode>,
		UpdateClassifierRequestProps
	>({
		mutationFn: updateClassifierRequest,
	});

	const updateClassifierWithSuccessHandling = async (data: UpdateClassifierRequestProps) => {
		if (!dealId) {
			showToastV2({
				title: tAdminDashboard('classifier.errors.no-deal-id'),
				state: 'error',
			});
			return;
		}

		updateClassifier.mutate(data, {
			onSuccess: async () => {
				await queryClient.refetchQueries({
					queryKey: [
						formatUnicorn(API_PATHS.DEAL_DEALID_CLASSIFIERS_GET, {
							dealId,
						}),
					],
				});
				showToastV2({
					title: 'Saved',
					state: 'success',
				});
			},
			onError: (response: ApiWrapperError<ErrorCode>) => {
				showToastV2({
					title: response.error?.message ?? 'An error occurred',
					state: 'error',
				});
			},
		});
	};

	return {
		updateClassifier: updateClassifierWithSuccessHandling,
		isLoading: updateClassifier.isPending,
	};
};
